import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Alert, Snackbar, Chip, IconButton,
  CircularProgress, Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { eledgerService } from '../services/eledgerService';
import { emissionService } from '../services/emissionService';

function EmissionRecording() {
  const [emissions, setEmissions] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    e_ledger_id: '',
    emission_date: new Date().toISOString().split('T')[0],
    emission_scope: 'scope1',
    quantity: '',
    unit_id: 'kg',
    emission_factor_value: '',
    calculated_co2e_kg: '',
    recording_method: 'calculated',
    description: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [emissionsData, ledgersData] = await Promise.all([
        emissionService.getAll(),
        eledgerService.getAll({ status: 'active' })
      ]);
      setEmissions(emissionsData ?? []);
      setLedgers(ledgersData ?? []);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const calculateCO2e = () => {
    if (formData.quantity && formData.emission_factor_value) {
      const co2e = parseFloat(formData.quantity) * parseFloat(formData.emission_factor_value);
      setFormData({ ...formData, calculated_co2e_kg: co2e.toFixed(4) });
    }
  };

  const resetForm = () => {
    setFormData({
      e_ledger_id: '',
      emission_date: new Date().toISOString().split('T')[0],
      emission_scope: 'scope1',
      quantity: '',
      unit_id: 'kg',
      emission_factor_value: '',
      calculated_co2e_kg: '',
      recording_method: 'calculated',
      description: ''
    });
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await emissionService.record(formData);
      setOpenDialog(false);
      resetForm();
      setSnackbar({ open: true, message: 'Direct emission recorded successfully', severity: 'success' });
      loadData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error?.message || 'Failed to record emission',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getLedgerName = (eLedgerId) => {
    const ledger = ledgers.find(l => l.e_ledger_id === eLedgerId);
    return ledger ? ledger.ledger_name : eLedgerId;
  };

  const getScopeLabel = (scope) => {
    switch (scope) {
      case 'scope1': return 'Scope 1';
      case 'scope2': return 'Scope 2';
      case 'scope3_upstream': return 'Scope 3 Up';
      case 'scope3_downstream': return 'Scope 3 Down';
      default: return scope;
    }
  };

  const getScopeColor = (scope) => {
    switch (scope) {
      case 'scope1': return 'primary';
      case 'scope2': return 'secondary';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'success';
      case 'pending': return 'warning';
      case 'unverified': return 'default';
      case 'disputed': return 'error';
      default: return 'default';
    }
  };

  const getMethodLabel = (method) => {
    switch (method) {
      case 'measured': return 'Measured';
      case 'calculated': return 'Calculated';
      case 'estimated': return 'Estimated';
      default: return method;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Emission Recording</Typography>
          <Typography variant="body2" color="text.secondary">
            Record direct emissions (Scope 1/2) to debit an E-ledger
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          disabled={ledgers.length === 0}
        >
          Record Emission
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {ledgers.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No active E-ledgers found. Create an E-ledger first to record emissions.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>E-Ledger</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell align="right">Factor</TableCell>
              <TableCell align="right">CO2e (kg)</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {emissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>
                    No emissions recorded yet. Click "Record Emission" to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              emissions.map((emission) => (
                <TableRow key={emission.direct_emission_id}>
                  <TableCell>{getLedgerName(emission.e_ledger_id)}</TableCell>
                  <TableCell>{emission.emission_date}</TableCell>
                  <TableCell>
                    <Chip
                      label={getScopeLabel(emission.emission_scope)}
                      size="small"
                      color={getScopeColor(emission.emission_scope)}
                    />
                  </TableCell>
                  <TableCell align="right">{parseFloat(emission.quantity).toFixed(4)}</TableCell>
                  <TableCell>{emission.unit_id}</TableCell>
                  <TableCell align="right">{parseFloat(emission.emission_factor_value).toFixed(6)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {parseFloat(emission.calculated_co2e_kg).toFixed(4)}
                  </TableCell>
                  <TableCell>
                    <Chip label={getMethodLabel(emission.recording_method)} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={emission.verification_status || 'pending'}
                      size="small"
                      color={getStatusColor(emission.verification_status)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" title="View Details">
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Record Emission Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Direct Emission</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="E-Ledger"
            fullWidth
            margin="normal"
            required
            value={formData.e_ledger_id}
            onChange={(e) => setFormData({ ...formData, e_ledger_id: e.target.value })}
          >
            {ledgers.map((ledger) => (
              <MenuItem key={ledger.e_ledger_id} value={ledger.e_ledger_id}>
                {ledger.ledger_name} ({ledger.entity_type})
              </MenuItem>
            ))}
          </TextField>

          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <TextField
                type="date"
                label="Emission Date"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={formData.emission_date}
                onChange={(e) => setFormData({ ...formData, emission_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label="Emission Scope"
                fullWidth
                required
                value={formData.emission_scope}
                onChange={(e) => setFormData({ ...formData, emission_scope: e.target.value })}
              >
                <MenuItem value="scope1">Scope 1 (Direct)</MenuItem>
                <MenuItem value="scope2">Scope 2 (Indirect - Energy)</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={4}>
              <TextField
                type="number"
                label="Quantity"
                fullWidth
                required
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                inputProps={{ step: '0.0001', min: '0' }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Unit"
                fullWidth
                value={formData.unit_id}
                onChange={(e) => setFormData({ ...formData, unit_id: e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                type="number"
                label="Emission Factor"
                fullWidth
                required
                value={formData.emission_factor_value}
                onChange={(e) => setFormData({ ...formData, emission_factor_value: e.target.value })}
                onBlur={calculateCO2e}
                inputProps={{ step: '0.000001', min: '0' }}
                helperText="kg CO2e/unit"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <TextField
                type="number"
                label="Calculated CO2e (kg)"
                fullWidth
                required
                value={formData.calculated_co2e_kg}
                onChange={(e) => setFormData({ ...formData, calculated_co2e_kg: e.target.value })}
                inputProps={{ step: '0.0001', min: '0' }}
                helperText="Auto-calculated from quantity x factor"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label="Recording Method"
                fullWidth
                value={formData.recording_method}
                onChange={(e) => setFormData({ ...formData, recording_method: e.target.value })}
              >
                <MenuItem value="measured">Measured</MenuItem>
                <MenuItem value="calculated">Calculated</MenuItem>
                <MenuItem value="estimated">Estimated</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={2}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); resetForm(); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !formData.e_ledger_id || !formData.calculated_co2e_kg}
          >
            {submitting ? <CircularProgress size={24} /> : 'Record'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default EmissionRecording;
